import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateInvestorInformation } from 'src/store/User/user.slice';
import { PersonalInformationContainer, Banner, Container } from './biometrics.styles';
import ModalErrorUploadingInput from 'src/components/shared/modals/errorUploadingInput/errorUploadingInput';
import { Button } from 'src/utilities/UILibrary/UI/Button/Button';
import Card from 'src/utilities/UILibrary/UI/Card/Card';
import { RootState, useAppThunkDispatch } from 'src/store/store';
import { MetamapButton } from 'src/components/shared/ MetamapButton/MetamapButton';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { InputField } from 'src/utilities/UILibrary/Forms/InputField/InputField';
import economicActivitiesGeneral from 'src/constants/economicActivitiesGeneral';
import economicActivitiesMapping from 'src/constants/economicActivitiesMapping';
import { FormValues } from './biometrics.types';
import { ocupations } from 'src/constants/ocupations';
import { useManageSteps } from 'src/hooks/useManageSteps';

export default function Biometrics() {
  const [errorUploadingInput, setErrorUploadingInput] = useState(false);
  const [isMetamapCompleted, setIsMetamapCompleted] = useState(false);
  const [data, setData] = useState({});
  const user = useSelector((state: RootState) => state.user.currentUser);
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const { control, watch, handleSubmit } = useForm<FormValues>();
  const { nextStep } = useManageSteps();

  const ocupationOptions = ocupations.map((ocupation) => ({
    value: ocupation.code,
    label: ocupation.name
  }));

  const economicActivity = watch('economicActivity');

  const clientid = process.env.REACT_APP_METAMAP_CLIENTID;
  const flowid = process.env.REACT_APP_METAMAP_FLOWID;
  const metadata = {
    id: user.id,
    typeOfUser: 'investor',
    fixedLanguage: 'es'
  };

  const closeFlow = () => {
    navigate('/');
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const input = {
      type: 'investor_fisica_onboarding_primerPaso',
      input: {
        id: user.id,
        personalInformation: {
          RFC: data.RFC,
          generalActivity: data.economicActivity,
          specificActivity: data.specificEconomicActivity,
          ocupation: data.ocupation
        }
      }
    };

    try {
      await dispatch(updateInvestorInformation(input));

      await nextStep();
      navigate('/inversionista/onboarding-risks');
    } catch (error) {
      console.error(error);
      setErrorUploadingInput(true);
    }
  };

  const optionsBusinessName = economicActivitiesGeneral.map((activity) => ({
    value: activity,
    label: activity
  }));

  return (
    <PersonalInformationContainer>
      <ModalErrorUploadingInput
        showModalErrorUploadingInput={errorUploadingInput}
        setErrorUploadingInput={setErrorUploadingInput}
      />
      <Banner>
        <div>
          <figure>
            <img
              src={`${process.env.REACT_APP_BUCKET_FINSPHERA}/icon_app_dash_personalInformation_banner.png`}
              alt="Finsphera icono información personal"
            />
          </figure>
          <h2>Datos personales y biométricos</h2>
        </div>
      </Banner>
      <Card className="card-container" isSlim isForm>
        <Card.Row>
          <p>
            Para iniciar con tu solicitud, es necesario que nos proporciones tus datos, debes de
            tener tu INE a la mano:
          </p>
        </Card.Row>
        <Card.Row>
          <MetamapButton
            disabled={isMetamapCompleted}
            clientid={clientid}
            flowid={flowid}
            metadata={`{"id":"${metadata.id}","typeOfUser":"${metadata.typeOfUser}"}`}
            onUserFinishedSdk={() => setIsMetamapCompleted(true)}
          />
        </Card.Row>

        <Card.Row>
          <h2>Ahora necesitamos tu RFC e información acerca de tus actividades económicas:</h2>
        </Card.Row>

        <Card.Row>
          <Controller
            name="RFC"
            control={control}
            rules={{
              minLength: {
                value: 13,
                message: 'RFC debe tener al menos 13 caracteres.'
              },
              required: 'Es un campo requerido.',
              pattern: {
                value: /^[A-ZÑ0-9]+$/,
                message: 'RFC inválido.'
              }
            }}
            render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
              <InputField
                label="Ingresa tu RFC:"
                placeholder="RFC"
                maxLength={13}
                autoUpperCase
                onChangeInternalValue={onChange}
                errorMessages={error && error.message ? [error.message] : []}
                {...rest}
              />
            )}
          />
        </Card.Row>
        <Card.Row>
          <Controller
            name="economicActivity"
            control={control}
            defaultValue={undefined}
            rules={{ required: 'Es un campo requerido.' }}
            render={({ field, fieldState, ...rest }) => (
              <InputField
                label="Principal actividad económica:"
                placeholder="Selecciona una opción"
                isDropdown
                options={optionsBusinessName}
                onChangeInternalValue={field.onChange}
                errorMessages={
                  fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                }
                {...rest}
              />
            )}
          />
        </Card.Row>

        {economicActivity && economicActivitiesMapping[economicActivity] && (
          <Card.Row>
            <Controller
              name="specificEconomicActivity"
              control={control}
              defaultValue={undefined}
              rules={{ required: 'Es un campo requerido.' }}
              render={({ field, fieldState, ...rest }) => (
                <InputField
                  label="Selecciona tu actividad específica:"
                  placeholder="Selecciona una opción"
                  isDropdown
                  options={economicActivitiesMapping[economicActivity].map(({ name }) => ({
                    value: name,
                    label: name
                  }))}
                  onChangeInternalValue={field.onChange}
                  errorMessages={
                    fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                  }
                  {...rest}
                />
              )}
            />
          </Card.Row>
        )}
        <Card.Row>
          <Controller
            name="ocupation"
            control={control}
            defaultValue={undefined}
            rules={{ required: 'Es un campo requerido.' }}
            render={({ field, fieldState, ...rest }) => (
              <InputField
                label="Ocupación:"
                placeholder="Selecciona una opción"
                isDropdown
                options={ocupationOptions}
                onChangeInternalValue={field.onChange}
                errorMessages={
                  fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                }
                {...rest}
              />
            )}
          />
        </Card.Row>
      </Card>
      <Container>
        <Button size="small" onClick={closeFlow}>
          Cerrar
        </Button>
        <Button disabled={!isMetamapCompleted} size="small" onClick={handleSubmit(onSubmit)}>
          Enviar
        </Button>
      </Container>
      <Container>
        <p>Si lo prefieres, puedes terminar la sincronización después.</p>
      </Container>
    </PersonalInformationContainer>
  );
}
